// src/pages/taxi_verification_screens/VehicleNotMatchedInfoScreen.js
import React, { useContext, useMemo, useState, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  ActivityIndicator,
  Pressable,
  Alert,
  Platform,
} from "react-native";
import axios from "axios";
import { useNavigation, useRoute } from "@react-navigation/native";
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';

import Header from "../../components/Header";
import { AppContext } from "../../../App";
import { useTranslation } from "../../i18n";

const API_BASE_URL = "https://api.taxi-yaounde.com";
// const API_BASE_URL = "http://localhost:4000";

const COLORS = {
  primary: "#14B8A6",
  background: "#F7F9FA",
  card: "#FFFFFF",
  textDark: "#1C1C1E",
  textMuted: "#6B7280",
  error: "#DC2626",
  border: "#E5E7EB",
  success: "#10B981",
};

export default function VehicleNotMatchedInfoScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { t } = useTranslation();
  const { verification, setVerification, setFormProgress } = useContext(AppContext);
  
  const identifier = (route.params?.identifier || "").toString().trim().toUpperCase();
  const existingOwnerName = route.params?.existingOwnerName || "";
  const verificationId = verification?.verificationId;

  const [ownerType, setOwnerType] = useState("PHYSIQUE");
  const [ownerNameOrCompany, setOwnerNameOrCompany] = useState("");
  const [ownerFirstName, setOwnerFirstName] = useState("");
  const [ownerLastName, setOwnerLastName] = useState("");
  const [ownerGender, setOwnerGender] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerCin, setOwnerCin] = useState("");
  const [ownerTaxpayerNumber, setOwnerTaxpayerNumber] = useState("");
  const [ownerSyndicate, setOwnerSyndicate] = useState("");

  
  const [idCardDocument, setIdCardDocument] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  const [errorMessage, setErrorMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Function to split existing owner name into first and last name
  const splitOwnerName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') {
      return { firstName: "", lastName: "" };
    }
    
    // Trim the name and split by spaces
    const trimmedName = fullName.trim();
    const nameParts = trimmedName.split(/\s+/);
    
    // If there's only one part, treat it as last name
    if (nameParts.length === 1) {
      return { firstName: "", lastName: nameParts[0] };
    }
    
    // If there are multiple parts, take the first part as first name
    // and join the rest as last name (to handle multi-part last names)
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');
    
    return { firstName, lastName };
  };

  // Use effect to prefill first and last name when component mounts
  useEffect(() => {
    if (existingOwnerName && ownerType === "PHYSIQUE") {
      const { firstName, lastName } = splitOwnerName(existingOwnerName);
      setOwnerFirstName(firstName);
      setOwnerLastName(lastName);
    }
  }, [existingOwnerName, ownerType]);

  // Also prefill company name if owner type is MORALE
  useEffect(() => {
    if (existingOwnerName && ownerType === "MORALE") {
      setOwnerNameOrCompany(existingOwnerName);
    }
  }, [existingOwnerName, ownerType]);

  const canSubmit = useMemo(() => {
    if (!identifier) return false;
    if (!ownerPhone.trim()) return false;
    if (ownerType === "PHYSIQUE") {
      if (!ownerFirstName.trim() || !ownerLastName.trim()) return false;
      if (!ownerGender) return false;
    } else {
      if (!ownerNameOrCompany.trim()) return false;
    }
    // Require ID card for verification
    // if (!idCardDocument) return false;
    return true;
  }, [identifier, ownerType, ownerFirstName, ownerLastName, ownerGender, ownerPhone, ownerNameOrCompany]);

  const pickDocument = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['image/*', 'application/pdf'],
        copyToCacheDirectory: true,
      });

      if (result.canceled) return;

      const file = result.assets[0];
      setIdCardDocument({
        uri: file.uri,
        name: file.name,
        type: file.mimeType || 'application/octet-stream',
        size: file.size,
      });
    } catch (error) {
      console.error('Error picking document: - VehicleNotMatchedInfoScreen.js:136', error);
      Alert.alert("Error", "Failed to pick document");
    }
  };

  const takePhoto = async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    
    if (status !== 'granted') {
      Alert.alert("Permission Required", "Camera permission is required to take photos.");
      return;
    }

    const result = await ImagePicker.launchCameraAsync({
      allowsEditing: true,
      quality: 0.8,
      base64: false,
    });

    if (!result.canceled) {
      const photo = result.assets[0];
      setIdCardDocument({
        uri: photo.uri,
        name: `id_card_${Date.now()}.jpg`,
        type: 'image/jpeg',
        size: photo.fileSize || 0,
      });
    }
  };

  const uploadIdCard = async (verificationId) => {
    if (!idCardDocument) return null;

    setUploadingDocument(true);
    
    try {
      const formData = new FormData();
      formData.append('document', {
        uri: idCardDocument.uri,
        name: idCardDocument.name,
        type: idCardDocument.type,
      });

      const response = await axios.post(
        `${API_BASE_URL}/api/verification/${verificationId}/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          params: {
            docType: 'OWNER_ID_CARD',
          },
        }
      );

      if (response.data.success) {
        return response.data.docId;
      }
      return null;
    } catch (error) {
      console.error('Error uploading ID card: - VehicleNotMatchedInfoScreen.js:197', error);
      return null;
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleSubmit = async () => {
    setErrorMessage(null);

    if (!canSubmit) {
      setErrorMessage(t("common.required"));
      return;
    }

    if (!verificationId) {
      setErrorMessage("Verification ID not found. Please go back and restart.");
      return;
    }

    setIsSubmitting(true);

    try {
      // First upload the ID card
      // const idCardDocumentId = await uploadIdCard(verificationId);
      
      // if (!idCardDocumentId) {
      //   throw new Error("Failed to upload ID card");
      // }

      // Prepare new owner data
      const newOwnerData = {
        type: ownerType,
        phone: ownerPhone.trim(),
        email: ownerEmail.trim() || null,
        cinNumber: ownerCin.trim() || null,
        taxpayerNumber: ownerTaxpayerNumber.trim() || null,
        ownerSyndicate: ownerSyndicate.trim() || null,
      };

      if (ownerType === "PHYSIQUE") {
        newOwnerData.fullName = `${ownerFirstName.trim()} ${ownerLastName.trim()}`.trim();
        newOwnerData.firstName = ownerFirstName.trim();
        newOwnerData.lastName = ownerLastName.trim();
        newOwnerData.gender = ownerGender;
      } else {
        newOwnerData.organizationName = ownerNameOrCompany.trim();
      }

      // Existing owner data from the search result
      const existingOwnerData = {
        name: existingOwnerName,
        // You could add more existing owner data here if available
      };

      // Submit owner mismatch
      const response = await axios.put(
        `${API_BASE_URL}/api/verification/${verificationId}/owner-mismatch`,
        {
          newOwner: newOwnerData,
          existingOwnerData,
          idCardDocumentId: null, // Include document ID if uploaded
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to process owner mismatch");
      }

      // Update verification context
      // setVerification((prev) => ({
      //   ...prev,
      //   owner: newOwnerData,
      // }));
      setVerification((prev) => ({
        ...prev,
        owner: {
          ...prev.owner,
          type: ownerType,
          ...(ownerType === "PHYSIQUE" ? {
            firstName: ownerFirstName.trim(),
            lastName: ownerLastName.trim(),
            fullName: `${ownerFirstName.trim()} ${ownerLastName.trim()}`.trim(),
            gender: ownerGender,
          } : {
            organizationName: ownerNameOrCompany.trim(),
          }),
          phone: ownerPhone.trim(),
          email: ownerEmail.trim(),
          cinNumber: ownerCin.trim(),
          taxpayerNumber: ownerTaxpayerNumber.trim(),
          ownerSyndicate: ownerSyndicate.trim(),
        },
        // Store ID card document reference
        pendingUploads: {
          ...prev.pendingUploads,
          ownerId: idCardDocument,
        },
      }));

      // Update form progress
      setFormProgress((prev) => ({
        ...prev,
        completedSteps: [
          ...new Set([
            ...(prev.completedSteps || []),
            "find_immatriculation_or_chasis_number_screen",
            "vehicle_not_matched_info",
          ]),
        ],
        currentStep: "vehicle_admin_info",
      }));

      // Navigate to vehicle admin info
      // navigation.navigate("vehicle_admin_info", {
      //   verificationId,
      //   vehicleId: response.data.vehicleId,
      //   status: "owner_mismatch_resolved",
      // });
      navigation.navigate("vehicle_admin_info", {
        verificationId,
        vehicleId: response.data.vehicleId,
        status: "owner_mismatch_resolved",
        cameFromNotMatched: true, // Pass this flag to indicate we came from the not matched flow
        matchedDriver: route.params?.matchedDriver, // ✅ Forward it!
        isDriverWorkflow: route.params?.isDriverWorkflow,
      });

    } catch (error) {
      console.error("Owner mismatch error: - VehicleNotMatchedInfoScreen.js:326", error);
      const msg = error.response?.data?.error || 
                  error.response?.data?.details || 
                  error.message || 
                  t("vehicleNotFoundForm.serverError");
      setErrorMessage(msg);
      Alert.alert("Error", msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigation.navigate("find_immatriculation_or_chasis_number_screen");
  };

  return (
    <View style={styles.screen}>
      <Header />
      <ScrollView contentContainerStyle={styles.scroll}>
        <Pressable style={styles.backButton} onPress={handleBack}>
          <Text style={styles.backIcon}>←</Text>
          {/* <Text style={styles.backText}>{t("common.back")}</Text> */}
        </Pressable>

        <Text style={styles.title}>{t("vehicleNotMatched.title", "Propriétaire non correspondant")}</Text>
        <Text style={styles.subtitle}>
          {t("vehicleNotMatched.subtitle", "Les informations du propriétaire ne correspondent pas. Veuillez fournir vos informations complètes.")}
        </Text>

        <View style={styles.card}>
          <Text style={styles.sectionTitle}>{t("vehicleNotMatched.enteredIdentifierTitle", "Identifiant du véhicule")}</Text>
          <Text style={styles.readonlyValue}>{identifier || "-"}</Text>
{/*           
          {existingOwnerName && (
            <>
              <Text style={styles.sectionTitle}>{t("vehicleNotMatched.existingOwnerTitle", "Propriétaire enregistré")}</Text>
              <Text style={styles.readonlyValue}>{existingOwnerName}</Text>
            </>
          )} */}
        </View>

        <View style={styles.card}>
          <Text style={styles.sectionTitle}>{t("vehicleNotMatched.newOwnerTitle", "Nouvelles informations du propriétaire")}</Text>

          <Text style={styles.label}>{t("vehicleNotMatched.ownerTypeLabel", "Type de propriétaire")}</Text>
          <View style={styles.row}>
            <Pill
              active={ownerType === "PHYSIQUE"}
              label={t("vehicleNotFoundForm.ownerTypePhysical", "Personne physique")}
              onPress={() => setOwnerType("PHYSIQUE")}
            />
            <Pill
              active={ownerType === "MORALE"}
              label={t("vehicleNotFoundForm.ownerTypeCompany", "Entreprise")}
              onPress={() => {
                setOwnerType("MORALE");
                setOwnerGender("");
              }}
            />
          </View>

          {ownerType === "PHYSIQUE" ? (
            <>
              <Text style={styles.label}>{t("ownerForm.firstName", "Prénom")} *</Text>
              <TextInput
                value={ownerFirstName}
                onChangeText={setOwnerFirstName}
                placeholder={t("ownerForm.firstNamePlaceholder", "Ex: Jean")}
                placeholderTextColor="#9CA3AF"
                style={styles.input}
              />

              <Text style={styles.label}>{t("ownerForm.lastName", "Nom")} *</Text>
              <TextInput
                value={ownerLastName}
                onChangeText={setOwnerLastName}
                placeholder={t("ownerForm.lastNamePlaceholder", "Ex: Dupont")}
                placeholderTextColor="#9CA3AF"
                style={styles.input}
              />

              <Text style={styles.label}>{t("vehicleNotFoundForm.sexLabel", "Sexe")} *</Text>
              <View style={styles.row}>
                <Pill
                  active={ownerGender === "Homme / Male"}
                  label={t("vehicleNotFoundForm.male", "Homme")}
                  onPress={() => setOwnerGender("Homme / Male")}
                />
                <Pill
                  active={ownerGender === "Femme / Female"}
                  label={t("vehicleNotFoundForm.female", "Femme")}
                  onPress={() => setOwnerGender("Femme / Female")}
                />
              </View>
            </>
          ) : (
            <>
              <Text style={styles.label}>{t("vehicleNotFoundForm.ownerNameLabelCompany", "Nom de l'entreprise")} *</Text>
              <TextInput
                value={ownerNameOrCompany}
                onChangeText={setOwnerNameOrCompany}
                placeholder={t("vehicleNotFoundForm.ownerNamePlaceholderCompany", "Ex: ETS NGONO")}
                placeholderTextColor="#9CA3AF"
                style={styles.input}
              />
            </>
          )}

          <Text style={styles.label}>{t("vehicleNotFoundForm.phoneLabel", "Téléphone")} *</Text>
          <TextInput
            value={ownerPhone}
            onChangeText={setOwnerPhone}
            keyboardType="phone-pad"
            placeholder={t("vehicleNotFoundForm.phonePlaceholder", "Ex: 6XX XXX XXX")}
            placeholderTextColor="#9CA3AF"
            style={styles.input}
          />

          <Text style={styles.label}>{t("vehicleNotFoundForm.emailLabel", "Email")}</Text>
          <TextInput
            value={ownerEmail}
            onChangeText={setOwnerEmail}
            keyboardType="email-address"
            placeholder={t("vehicleNotFoundForm.emailPlaceholder", "Ex: jean.dupont@email.com")}
            placeholderTextColor="#9CA3AF"
            style={styles.input}
          />

          <Text style={styles.label}>{t("driverManager.fields.cinNumber", "Numéro CIN")}</Text>
          <TextInput
            value={ownerCin}
            onChangeText={setOwnerCin}
            placeholder={t("driverManager.fields.cinNumber", "Numéro CIN")}
            placeholderTextColor="#9CA3AF"
            style={styles.input}
          />

          <Text style={styles.label}>{t("driverManager.fields.taxpayerNumber", "Numéro Contribuable")}</Text>
          <TextInput
            value={ownerTaxpayerNumber}
            onChangeText={setOwnerTaxpayerNumber}
            placeholder={t("driverManager.fields.taxpayerNumber", "Numéro Contribuable")}
            placeholderTextColor="#9CA3AF"
            style={styles.input}
          />
        </View>
        <Text style={styles.label}>{t("vehicleNotMatched.ownerSyndicateLabel", "Syndicat du propriétaire")} *</Text>
        <TextInput
          value={ownerSyndicate}
          onChangeText={setOwnerSyndicate}
          placeholder={t("vehicleNotMatched.ownerSyndicatePlaceholder", "Ex: Syndicat des transporteurs")}
          placeholderTextColor="#9CA3AF"
          style={styles.input}
        />

        {/* <View style={styles.card}>
          <Text style={styles.sectionTitle}>{t("vehicleNotMatched.idCardTitle", "Pièce d'identité")} *</Text>
          
          {idCardDocument ? (
            <View style={styles.documentPreview}>
              <Text style={styles.documentName}>
                📄 {idCardDocument.name} ({Math.round(idCardDocument.size / 1024)} KB)
              </Text>
              <TouchableOpacity onPress={() => setIdCardDocument(null)}>
                <Text style={styles.removeDocument}>{t("common.remove", "Supprimer")}</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.documentButtons}>
              <TouchableOpacity style={styles.documentButton} onPress={takePhoto}>
                <Text style={styles.documentButtonText}>📸 {t("vehicleNotMatched.takePhoto", "Prendre une photo")}</Text>
              </TouchableOpacity>
              
              <TouchableOpacity style={styles.documentButton} onPress={pickDocument}>
                <Text style={styles.documentButtonText}>📁 {t("vehicleNotMatched.uploadFile", "Importer un fichier")}</Text>
              </TouchableOpacity>
            </View>
          )}
          
          <Text style={styles.helperText}>
            {t("vehicleNotMatched.idCardHelper", "Format accepté : PDF, JPG, PNG")}
          </Text>
        </View> */}

        {errorMessage && <Text style={styles.errorText}>{errorMessage}</Text>}

        <TouchableOpacity
          onPress={handleSubmit}
          disabled={!canSubmit || isSubmitting || uploadingDocument}
          style={[styles.cta, (!canSubmit || isSubmitting || uploadingDocument) && { opacity: 0.5 }]}
        >
          {(isSubmitting || uploadingDocument) ? (
            <ActivityIndicator color="#FFF" />
          ) : (
            <Text style={styles.ctaText}>{t("common.continue", "Continuer")}</Text>
          )}
        </TouchableOpacity>

        <View style={{ height: 30 }} />
      </ScrollView>
    </View>
  );
}

function Pill({ active, label, onPress }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={[styles.pill, active && styles.pillActive]}
    >
      <Text style={[styles.pillText, active && styles.pillTextActive]}>{label}</Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: COLORS.background },
  scroll: { paddingHorizontal: 16, paddingBottom: 40 },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    width: 120,
    height: 42,
    borderRadius: 12,
    backgroundColor: "#fff",
    paddingHorizontal: 12,
    marginBottom: 8,
    marginTop: 16,
  },
  backIcon: { fontSize: 18, color: COLORS.textDark, marginRight: 6 },
  backText: { fontSize: 14, color: COLORS.textDark, fontWeight: "600" },
  title: { 
    fontSize: 22, 
    fontWeight: "800", 
    textAlign: "center", 
    marginBottom: 6, 
    color: COLORS.textDark 
  },
  subtitle: { 
    textAlign: "center", 
    color: COLORS.textMuted, 
    marginBottom: 18,
    fontSize: 14,
    lineHeight: 20,
  },
  card: {
    backgroundColor: COLORS.card,
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: COLORS.border,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOpacity: 0.05,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  sectionTitle: { 
    fontSize: 16, 
    fontWeight: "800", 
    marginBottom: 12, 
    color: COLORS.textDark,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    paddingBottom: 8,
  },
  readonlyValue: { 
    fontSize: 16, 
    fontWeight: "700", 
    color: COLORS.textDark,
    padding: 10,
    backgroundColor: COLORS.background,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  label: { 
    marginTop: 10, 
    marginBottom: 6, 
    color: COLORS.textMuted, 
    fontWeight: "600",
    fontSize: 13,
  },
  input: {
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: "#F9FAFB",
    color: COLORS.textDark,
    height: 48,
    fontSize: 14,
  },
  row: { 
    flexDirection: "row", 
    gap: 10, 
    marginTop: 6, 
    flexWrap: "wrap" 
  },
  pill: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: "#fff",
  },
  pillActive: { 
    backgroundColor: COLORS.primary, 
    borderColor: COLORS.primary 
  },
  pillText: { 
    color: COLORS.textDark, 
    fontWeight: "700", 
    fontSize: 14 
  },
  pillTextActive: { 
    color: "#fff" 
  },
  documentButtons: {
    flexDirection: "row",
    gap: 12,
    marginVertical: 8,
  },
  documentButton: {
    flex: 1,
    padding: 14,
    borderRadius: 12,
    backgroundColor: COLORS.background,
    borderWidth: 1,
    borderColor: COLORS.border,
    alignItems: "center",
  },
  documentButtonText: {
    fontSize: 14,
    fontWeight: "600",
    color: COLORS.textDark,
  },
  documentPreview: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    backgroundColor: COLORS.background,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: COLORS.border,
    marginVertical: 8,
  },
  documentName: {
    flex: 1,
    fontSize: 14,
    color: COLORS.textDark,
  },
  removeDocument: {
    color: COLORS.error,
    fontWeight: "600",
    fontSize: 14,
  },
  helperText: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginTop: 6,
  },
  errorText: { 
    color: COLORS.error, 
    textAlign: "center", 
    marginTop: 8, 
    marginBottom: 6,
    fontSize: 12,
  },
  cta: {
    marginTop: 20,
    backgroundColor: COLORS.primary,
    paddingVertical: 16,
    borderRadius: 14,
    alignItems: "center",
    shadowColor: COLORS.primary,
    shadowOpacity: 0.2,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 4,
  },
  ctaText: { 
    color: "#fff", 
    fontWeight: "800", 
    fontSize: 16 
  },
});